import { useRef } from 'react';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { useIsAltPressed } from '../../hooks/useAltKeyPressed';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useFlow } from '../../store/flow/useFlows.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { ContextMenuContent } from '../../components/common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../../components/common/context-menu/DeveloperMenu';
import { promptMessage } from '../../components/overlay/DialogPrompt';

export function FlowNavigationItemFlowContextMenuContent(props) {
  const { i18n } = useI18n();
  // const ctx = useContextMenuContext();

  const pendingRef = useRef({
    favorite: false,
  });
  const { api } = useApi();
  const { flow } = useFlow({ flowId: props.nodeId });

  const { userMe } = useUserMe();
  const isFavoriteFlow = userMe?.properties.favoriteFlows?.includes(flow.id) ?? false;

  const isAltPressed = useIsAltPressed();

  const toggleText = flow.enabled
    ? i18n.messageFormatter(`flow.disable`)
    : i18n.messageFormatter(`flow.enable`);

  async function onAction(key) {
    switch (key) {
      case 'trigger':
        try {
          await api.flow.triggerFlow({ id: flow.id });
          ToastManager.add({
            icon: glyph.system.flow,
            message: i18n.messageFormatter(`flow.started`, {
              name: flow.name,
            }),
          });
        } catch (error) {
          ToastManager.handleError(error);
        }

        break;
      case 'toggle':
        api.flow
          .updateFlow({ id: flow.id, flow: { enabled: !flow.enabled } })
          .catch(ToastManager.handleError);
        break;

      case 'favorite': {
        (async () => {
          const favoriteName = `**${flow.name}**`;

          if (pendingRef.current.favorite === true) {
            ToastManager.add({
              message: 'Request still pending, please wait...',
            });
            return;
          }

          if (isFavoriteFlow) {
            try {
              pendingRef.current.favorite = true;

              const nextUserMe = {
                ...userMe,
                properties: {
                  ...userMe.properties,
                  favoriteFlows: (userMe.properties.favoriteFlows ?? []).filter(
                    (id) => id !== props.nodeId,
                  ),
                },
              };
              userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

              await api.users.updateUserMeProperties({
                id: 'favoriteFlows',
                value: nextUserMe.properties.favoriteFlows,
              });

              ToastManager.add({
                icon: glyph.core.heartOutline,
                iconColor: theme.icon.color_light,
                message: i18n.messageFormatter(`flow.removeFavorite`, {
                  name: favoriteName,
                }),
              });
              userMeDraftStore.setUserMeDraftDone();
            } catch (error) {
              userMeDraftStore.setUserMeDraftFailed();
              ToastManager.handleError(error);
            } finally {
              pendingRef.current.favorite = false;
            }

            return;
          }

          try {
            pendingRef.current.favorite = true;

            const nextUserMe = {
              ...userMe,
              properties: {
                ...userMe.properties,
                favoriteFlows: [...(userMe.properties.favoriteFlows ?? []), props.nodeId],
              },
            };
            userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

            await api.users.updateUserMeProperties({
              id: 'favoriteFlows',
              value: nextUserMe.properties.favoriteFlows,
            });

            ToastManager.add({
              icon: glyph.core.heartFill,
              iconColor: theme.color.special_favorite,
              message: i18n.messageFormatter(`flow.addFavorite`, {
                name: favoriteName,
              }),
            });
            userMeDraftStore.setUserMeDraftDone();
          } catch (error) {
            userMeDraftStore.setUserMeDraftFailed();
            ToastManager.handleError(error);
          } finally {
            pendingRef.current.favorite = false;
          }
        })().catch(ToastManager.handleError);
        break;
      }

      case 'share':
        try {
          const shareWindow = window.open();

          const result = await api.flow.shareFlow({ id: flow.id });

          // If it somehow fails.
          if (shareWindow == null) {
            window.location.href = result.url;
            return;
          }

          shareWindow.location.replace(result.url);
        } catch (error) {
          ToastManager.handleError(error);
        }

        break;
      case 'duplicate':
        try {
          const result = await api.flow.createFlow({
            flow: {
              name: `${flow.name} (${i18n.messageFormatter('common.copyNoun')})`,
              folder: flow.folder,
              enabled: flow.enabled,
              trigger: flow.trigger,
              conditions: flow.conditions,
              actions: flow.actions,
            },
          });

          RouteManager.toFlow(result.id, {
            focus: 'name',
          });
        } catch (error) {
          ToastManager.handleError(error);
        }

        break;
      case 'export': {
        const filename = `${flow.name || flow.id}.homeystdflow`;
        const filedata = JSON.stringify({
          type: 'standard',
          name: flow.name,
          trigger: flow.trigger,
          conditions: flow.conditions,
          actions: flow.actions,
        });

        // Use the File System Access API if available, otherwise fallback to Blob
        if (window.showSaveFilePicker) {
          Promise.resolve()
            .then(async () => {
              const handle = await window.showSaveFilePicker({
                suggestedName: filename,
                types: [
                  {
                    description: 'Flow Export',
                    accept: { 'application/json': ['.homeystdflow'] },
                  },
                ],
              });

              const writable = await handle.createWritable();
              await writable.write(filedata);
              await writable.close();
            })
            .catch((err) => {
              if (err.name === 'AbortError') return;
              alert(`Error Exporting Flow: ${err.message}`);
            });
        } else {
          const blob = new Blob([filedata], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        }

        break;
      }
      case 'rename':
        props.onRenameRequest();
        break;
      case 'delete':
        try {
          await promptMessage({
            message: i18n.messageFormatter('prompt.delete', { name: flow.name }),
          });

          try {
            await api.flow.deleteFlow({ id: flow.id });
          } catch (error) {
            ToastManager.handleError(error);
          }
        } catch {}

        // props.onDeleteRequest();
        break;
      default:
        break;
    }
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.start')}
        icon={glyph.core.play}
        isDisabled={flow?.triggerable === false}
        onPress={() => {
          onAction('trigger');
        }}
      />
      <ContextMenuContent.ListItem
        label={toggleText}
        onPress={() => {
          onAction('toggle');
        }}
      />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.rename')}
        onPress={() => {
          onAction('rename');
        }}
      />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.favorite')}
        icon={isFavoriteFlow ? glyph.core.heartFill : glyph.core.heartOutline}
        onPress={() => {
          onAction('favorite');
        }}
      />
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.share')}
        icon={glyph.core.squareArrowUp}
        onPress={() => {
          onAction('share');
        }}
      />
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.duplicate')}
        icon={glyph.core.squareFilledOnSquare}
        onPress={() => {
          onAction('duplicate');
        }}
      />

      {isAltPressed === true && (
        <ContextMenuContent.ListItem
          label={i18n.messageFormatter('flow.export')}
          icon={glyph.core.arrowDownToLine}
          onPress={() => {
            onAction('export');
          }}
        />
      )}

      <DeveloperMenu onIdRequest={() => flow?.id} onResourceRequest={() => flow} />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.delete')}
        variant="danger"
        icon={glyph.core.trashFill}
        onPress={() => {
          onAction('delete');
        }}
      />
    </ContextMenuContent.List>
  );
}
